/**
 * Defines versioned worker jobs, SLA alerts and durable notification recovery.
 */
import { z } from "zod";
export const SlaThresholdReachedEventName = "sla.threshold-reached.v1";
export const SlaThresholdReachedEventPayloadSchema = z
    .object({
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    companyId: z.uuid(),
    dueDate: z.iso.date(),
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
    remainingBusinessDays: z.number().int(),
})
    .strict();
export const SlaEscalationLevelReachedEventName = "sla.escalation-level-reached.v1";
/**
 * Carries the ladder step that was reached. It is a separate event from
 * sla.threshold-reached.v1 because that payload is strict and already has rows
 * persisted in outbox_events that an extra field would fail to parse.
 */
export const SlaEscalationLevelReachedEventPayloadSchema = z
    .object({
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    companyId: z.uuid(),
    dueDate: z.iso.date(),
    escalationLevel: z.number().int().min(1).max(20),
    escalationLevelId: z.uuid(),
    escalationTitle: z.string().min(1).max(120),
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
    remainingBusinessDays: z.number().int(),
})
    .strict();
/**
 * Minimal non-PII envelope copied from PostgreSQL to Redis.
 */
export const OutboxJobSchema = z
    .object({
    companyId: z.uuid(),
    eventId: z.uuid(),
    eventType: z.string().min(1).max(120),
    eventVersion: z.number().int().positive(),
    occurredAt: z.iso.datetime({ offset: true }),
    payload: z.record(z.string(), z.unknown()),
})
    .strict();
export const NotificationTypeSchema = z.enum([
    "CASE_CREATED",
    "CASE_CHANGED",
    "SLA_APPROACHING",
    "SLA_DUE",
    "SLA_ESCALATED",
    "SLA_OVERDUE",
    "DRAFT_PENDING",
    "DRAFT_APPROVED",
    "DRAFT_REJECTED",
    "FINAL_RESPONSE",
    "RESPONSE_DELIVERY_ERROR",
    "FOLLOW_UP_CREATED",
    "FOLLOW_UP_REMINDER_3D",
    "FOLLOW_UP_REMINDER_1D",
    "FOLLOW_UP_DUE",
    "FOLLOW_UP_OVERDUE_1H",
    "FOLLOW_UP_DAILY_OVERDUE",
    "FOLLOW_UP_RESCHEDULED",
    "COMPANY_ADMIN_PROVISIONED",
]);
export const NotificationListQuerySchema = z
    .object({
    casePublicId: z.string().regex(/^\d+$/).optional(),
    cursor: z
        .string()
        .regex(/^[A-Za-z0-9_-]+$/)
        .optional(),
    from: z.iso.date().optional(),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
    to: z.iso.date().optional(),
    unreadOnly: z
        .preprocess((value) => {
        if (value === true || value === "true") {
            return true;
        }
        if (value === false || value === "false" || value === undefined || value === "") {
            return false;
        }
        return value;
    }, z.boolean())
        .default(false),
})
    .strict()
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, "The start date must not be after the end date.");
export const NotificationParametersSchema = z
    .object({
    notificationId: z.uuid(),
})
    .strict();
export const NotificationItemSchema = z
    .object({
    branchId: z.uuid().nullable(),
    casePublicId: z.string().regex(/^\d+$/).nullable(),
    id: z.uuid(),
    message: z.string(),
    occurredAt: z.iso.datetime({ offset: true }),
    readAt: z.iso.datetime({ offset: true }).nullable(),
    title: z.string(),
    type: NotificationTypeSchema,
})
    .strict();
export const NotificationListResponseSchema = z
    .object({
    items: z.array(NotificationItemSchema),
    nextCursor: z.string().nullable(),
    unreadCount: z.number().int().nonnegative(),
})
    .strict();
export const NotificationReadResponseSchema = z
    .object({
    id: z.uuid(),
    readAt: z.iso.datetime({ offset: true }),
    wasAlreadyRead: z.boolean(),
})
    .strict();
export const NotificationMarkAllReadResponseSchema = z
    .object({
    markedCount: z.number().int().nonnegative(),
    readAt: z.iso.datetime({ offset: true }),
})
    .strict();
//# sourceMappingURL=AsyncContract.js.map